import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  LayoutSubSidebar,
  LayoutSubHeader,
  LayoutPageTitle,
} from '@/features/layout';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { EmptyState } from '@/shared/ui/EmptyState';
import {
  ReportFilterRail,
  ReportTable,
  useReportListFilters,
  type ReportTab,
} from '@/features/report-list';
import { useListAllReportsQuery } from '@/state/slices/reports';
import { usePageLoading } from '@/hooks/usePageLoading';
import styles from './Reports.module.css';

export default function Reports() {
  const navigate = useNavigate();

  const {
    params,
    countsParams,
    activeTab,
    onTabChange,
    searchText,
    setSearchText,
    selectedSites,
    onSitesChange,
    selectedStudyTypes,
    onStudyTypesChange,
    datePreset,
    onDatePresetChange,
    customDateRange,
    onCustomDateRangeChange,
    selectedReferringPhysicians,
    onReferringPhysiciansChange,
    selectedInterpretingPhysicians,
    onInterpretingPhysiciansChange,
    selectedSonographers,
    onSonographersChange,
    hasActiveFilters,
    activeFilterCount,
    hasActiveSearch,
    clearFilters,
    clearSearch,
    onTableChange,
  } = useReportListFilters();

  const { data, isFetching } = useListAllReportsQuery(params);
  const { data: countsData } = useListAllReportsQuery(countsParams);

  const statusCounts = countsData?.statusCounts;

  usePageLoading(isFetching);

  const hasData = (data?.results?.length ?? 0) > 0;

  const renderEmptyState = () => {
    // 1. Combined: search + filters
    if (hasActiveSearch && hasActiveFilters) {
      return (
        <EmptyState
          headline="Your search and filters returned no results"
          subtext="Try adjusting your search or clearing your filters."
          action={{ label: 'Clear search', onClick: clearSearch }}
        >
          <Button type="link" onClick={clearFilters}>
            Reset filters
          </Button>
        </EmptyState>
      );
    }

    // 2. Search only
    if (hasActiveSearch) {
      return (
        <EmptyState
          headline="No matching reports"
          subtext="No reports matched your search. Try a different term."
          action={{ label: 'Clear search', onClick: clearSearch }}
        />
      );
    }

    // 3. Filters only
    if (hasActiveFilters) {
      return (
        <EmptyState
          headline="No matching reports"
          subtext="Try clearing or adjusting your filters."
          action={{ label: 'Reset filters', onClick: clearFilters }}
        />
      );
    }

    // 4. Tab-specific baselines
    if (activeTab === 'Final') {
      return (
        <EmptyState
          headline="No finalized reports"
          subtext="Finalized reports will appear here after signing."
          action={{ label: 'View drafts', onClick: () => onTabChange('Draft') }}
        >
          <Button type="link" onClick={() => onTabChange('All')}>
            View all
          </Button>
        </EmptyState>
      );
    }

    if (activeTab === 'Draft') {
      return (
        <EmptyState
          headline="No draft reports"
          subtext="Draft reports will appear here when started."
          action={{
            label: 'View finalized',
            onClick: () => onTabChange('Final'),
          }}
        >
          <Button type="link" onClick={() => onTabChange('All')}>
            View all
          </Button>
        </EmptyState>
      );
    }

    if (activeTab === 'Preliminary') {
      return (
        <EmptyState
          headline="No preliminary reports"
          subtext="Preliminary reports will appear here once reviewed."
          action={{ label: 'View all', onClick: () => onTabChange('All') }}
        />
      );
    }

    // 5. All tab, no data at all
    return (
      <EmptyState
        headline="No reports yet"
        subtext="Reports appear here after they are created for a study."
        action={{
          label: 'Go to Worklist',
          onClick: () => navigate('/worklist'),
        }}
      />
    );
  };

  return (
    <>
      <LayoutPageTitle title="Reports" />
      <LayoutSubSidebar title="Filters">
        <ReportFilterRail
          searchText={searchText}
          onSearchChange={setSearchText}
          selectedSites={selectedSites}
          onSitesChange={onSitesChange}
          selectedStudyTypes={selectedStudyTypes}
          onStudyTypesChange={onStudyTypesChange}
          datePreset={datePreset}
          onDatePresetChange={onDatePresetChange}
          customDateRange={customDateRange}
          onCustomDateRangeChange={onCustomDateRangeChange}
          selectedReferringPhysicians={selectedReferringPhysicians}
          onReferringPhysiciansChange={onReferringPhysiciansChange}
          selectedInterpretingPhysicians={selectedInterpretingPhysicians}
          onInterpretingPhysiciansChange={onInterpretingPhysiciansChange}
          selectedSonographers={selectedSonographers}
          onSonographersChange={onSonographersChange}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />
      </LayoutSubSidebar>

      <LayoutSubHeader>
        <div className={styles.subHeaderRow}>
          <Tabs
            value={activeTab}
            onValueChange={(v) => onTabChange(v as ReportTab)}
          >
            <TabsList>
              <TabsTrigger value="All" badge={countsData?.count ?? 0}>
                All
              </TabsTrigger>
              <TabsTrigger value="Draft" badge={statusCounts?.draft ?? 0}>
                Draft
              </TabsTrigger>
              <TabsTrigger
                value="Preliminary"
                badge={statusCounts?.preliminary ?? 0}
              >
                Preliminary
              </TabsTrigger>
              <TabsTrigger value="Final" badge={statusCounts?.final ?? 0}>
                Final
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {hasActiveFilters && (
            <div className={styles.filterIndicator}>
              <div className={styles.divider} />
              <span className={styles.filterDot} />
              <span className={styles.filterText}>
                {activeFilterCount}{' '}
                {activeFilterCount === 1 ? 'filter' : 'filters'} active
              </span>
              <button
                type="button"
                className={styles.resetButton}
                onClick={clearFilters}
              >
                Reset
              </button>
            </div>
          )}
        </div>
      </LayoutSubHeader>

      {hasData ? (
        <div className={styles.tableWrapper}>
          <ReportTable
            data={data}
            loading={false}
            page={params.page}
            pageSize={params.size}
            onTableChange={onTableChange}
          />
        </div>
      ) : (
        !isFetching && renderEmptyState()
      )}
    </>
  );
}
